import { translate, translateWithVars } from "shared/languages";
import { localDateOnly } from "shared/utils/dayjs";
import { ImpossibleDate } from "./buildAvailabilityDisabledTime";
import { formatNaturalList } from "./formatNaturalList";

// Groups sorted YYYY-MM-DD dates into runs of consecutive days, so e.g. two
// adjacent-but-separate leave records (Sept 1-3 and Sept 4-6) read as one
// clean range instead of two disjointed mentions.
const mergeContiguousDates = (dates: string[]): string[][] => {
  const sorted = [...dates].sort();
  const groups: string[][] = [];
  sorted.forEach((date) => {
    const lastGroup = groups[groups.length - 1];
    const lastDate = lastGroup?.[lastGroup.length - 1];
    if (lastDate && localDateOnly(date).diff(localDateOnly(lastDate), "day") === 1) {
      lastGroup.push(date);
    } else {
      groups.push([date]);
    }
  });
  return groups;
};

const formatDateRange = (group: string[]): string => {
  const start = localDateOnly(group[0]);
  if (group.length === 1) return start.format("D MMMM");
  const end = localDateOnly(group[group.length - 1]);
  return start.isSame(end, "month")
    ? `${start.format("D")}-${end.format("D MMMM")}`
    : `${start.format("D MMMM")} - ${end.format("D MMMM")}`;
};

/**
 * Builds the "no valid time" Alert message from the impossible-dates list —
 * names the actual blocking weekday(s)/date(s) when it can, rather than a
 * one-size-fits-all generic message. A date blocked by both reasons at once
 * is already categorized once under "no_working_hours" upstream (see
 * buildAvailabilityDisabledTime), so no double-counting risk here.
 *
 * `isRecurring` picks between two working-hours phrasings: "Tidak berulang"
 * (the default) means there's only ever one candidate date, so a sentence
 * referencing "this repeat pattern" reads as nonsensical — see the plain,
 * pattern-agnostic variant below. The other two message keys (on_leave,
 * combined) don't reference a pattern either way, so they're unaffected;
 * "combined" is also unreachable for a single date in practice (one date
 * can only ever carry one reason).
 */
export const buildNoValidTimeMessage = (
  impossibleDates: ImpossibleDate[],
  isRecurring: boolean,
): string => {
  const noWorkingHoursDates = impossibleDates
    .filter((d) => d.reason === "no_working_hours")
    .map((d) => d.date);
  const onLeaveDates = impossibleDates
    .filter((d) => d.reason === "on_leave")
    .map((d) => d.date);

  // Neither reason identifies every disabled hour (a dense stack of partial
  // busy-window conflicts, no single date inherently impossible) — fall
  // back to the generic message.
  if (noWorkingHoursDates.length === 0 && onLeaveDates.length === 0) {
    return translate("schedule.drawer.no_valid_time_generic");
  }

  // One representative date per distinct weekday, then sorted Monday-first
  // (not the chronological order dates happened to appear in the pattern —
  // a daily recurrence starting on a Friday would otherwise list "Jumat,
  // Sabtu, Minggu, Selasa, dan Kamis", which reads as out of order against
  // the calendar's own Monday-first week).
  const weekdayToDate = new Map<number, string>();
  noWorkingHoursDates.forEach((date) => {
    const weekday = localDateOnly(date).day();
    if (!weekdayToDate.has(weekday)) weekdayToDate.set(weekday, date);
  });
  const mondayFirstIndex = (weekday: number) => (weekday + 6) % 7;
  const days = formatNaturalList(
    Array.from(weekdayToDate.keys())
      .sort((a, b) => mondayFirstIndex(a) - mondayFirstIndex(b))
      .map((weekday) => localDateOnly(weekdayToDate.get(weekday) as string).format("dddd")),
  );
  const dates = formatNaturalList(mergeContiguousDates(onLeaveDates).map(formatDateRange));

  const hasNoWorkingHours = noWorkingHoursDates.length > 0;
  const hasOnLeave = onLeaveDates.length > 0;

  if (hasNoWorkingHours && !hasOnLeave) {
    return translateWithVars(
      isRecurring
        ? "schedule.drawer.no_valid_time_no_working_hours"
        : "schedule.drawer.no_valid_time_no_working_hours_single",
      { days },
    );
  }
  if (hasOnLeave && !hasNoWorkingHours) {
    return translateWithVars("schedule.drawer.no_valid_time_on_leave", { dates });
  }

  // Both reasons present — join short clause fragments, not the two full
  // standalone sentences, so the reader sees which dates fall under which
  // reason without a redundant, nested-sentence read.
  const reasons = [
    translateWithVars("schedule.drawer.no_valid_time_fragment_no_working_hours", { days }),
    translateWithVars("schedule.drawer.no_valid_time_fragment_on_leave", { dates }),
  ].join("; ");
  return translateWithVars("schedule.drawer.no_valid_time_combined", { reasons });
};
